import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { CommitteeDto, UpdateCommitteeDecisionDto, CommitteeDecisionStatus, committeesApi, handleApiError } from '../../services/committeesService';

interface UpdateDecisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  committee: CommitteeDto | null;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  direction: rtl;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;

  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? 'var(--error-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? 'var(--error-color)' : 'var(--primary-color)'};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const InfoBox = styled.div`
  background: #f0f7ff;
  border: 1px solid #008EA2;
  border-radius: var(--border-radius-md);
  padding: 12px;
  margin-bottom: 20px;
  font-size: 14px;
  color: var(--text-primary);
`;

const SuccessMessage = styled.div`
  background: rgba(22, 163, 74, 0.1);
  border: 1px solid #16a34a;
  border-radius: var(--border-radius-md);
  padding: 16px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #15803d;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  line-height: 1.6;

  &::before {
    content: '✓';
    font-size: 24px;
    font-weight: bold;
    color: #16a34a;
    flex-shrink: 0;
  }
`;

const Section = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);
`;

const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'secondary' ? 'var(--border-color)' : 'var(--primary-color)'};
  color: ${props => props.variant === 'secondary' ? 'var(--text-primary)' : 'white'};
  border: none;
  padding: 12px 24px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UpdateDecisionModal: React.FC<UpdateDecisionModalProps> = ({ isOpen, onClose, onSuccess, committee }) => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [formData, setFormData] = useState<UpdateCommitteeDecisionDto>({
    decisionStatus: CommitteeDecisionStatus.Pending,
    disabilityPercentage: undefined,
    approvalEndDate: undefined,
    monthlyBenefitAmount: undefined,
    retroactiveFromDate: undefined,
    retroactiveTotalAmount: undefined,
    decisionDocumentPath: undefined,
    decisionNotes: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (isOpen && committee) {
      setFormData({
        decisionStatus: committee.decisionStatus,
        disabilityPercentage: committee.disabilityPercentage,
        approvalEndDate: committee.approvalEndDate,
        monthlyBenefitAmount: committee.monthlyBenefitAmount,
        retroactiveFromDate: committee.retroactiveFromDate,
        retroactiveTotalAmount: committee.retroactiveTotalAmount,
        decisionDocumentPath: committee.decisionDocumentPath,
        decisionNotes: committee.decisionNotes || ''
      });
      setErrors({});
      setSuccessMessage('');
    }
  }, [isOpen, committee]);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    const isApproved = formData.decisionStatus === CommitteeDecisionStatus.ApprovedPermanent ||
                       formData.decisionStatus === CommitteeDecisionStatus.ApprovedForPeriod;

    if (isApproved) {
      if (!formData.disabilityPercentage) {
        newErrors.disabilityPercentage = 'יש להזין אחוז נכות';
      } else if (formData.disabilityPercentage < 0 || formData.disabilityPercentage > 100) {
        newErrors.disabilityPercentage = 'אחוז נכות חייב להיות בין 0 ל-100';
      }
    }

    // Require end date for period approval
    if (formData.decisionStatus === CommitteeDecisionStatus.ApprovedForPeriod && !formData.approvalEndDate) {
      newErrors.approvalEndDate = 'יש להזין תאריך סיום זכאות';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!committee) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const result = await committeesApi.updateDecision(committee.id, formData);

      // Display success message with reminder info if available
      if (result.reminderMessage) {
        setSuccessMessage(`החלטת הועדה עודכנה בהצלחה! ${result.reminderMessage}`);
      } else {
        setSuccessMessage('החלטת הועדה עודכנה בהצלחה!');
      }

      // Wait a moment to show the message, then close and refresh
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2500);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !committee) return null;

  const isApproved = formData.decisionStatus === CommitteeDecisionStatus.ApprovedPermanent ||
                     formData.decisionStatus === CommitteeDecisionStatus.ApprovedForPeriod;
  const isApprovedForPeriod = formData.decisionStatus === CommitteeDecisionStatus.ApprovedForPeriod;

  return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>עדכון החלטת ועדה</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {successMessage && (
              <SuccessMessage>
                <span>{successMessage}</span>
              </SuccessMessage>
            )}

            <InfoBox>
              מספר הערכה: {committee.evaluationNumber} מתוך 3
            </InfoBox>

            <FormGroup>
              <Label>תאריך הועדה</Label>
              <Input
                type="text"
                value={new Date(committee.committeeDate).toLocaleString('he-IL')}
                disabled
                style={{ background: 'var(--background-secondary)' }}
              />
            </FormGroup>

            <FormGroup>
              <Label>החלטה *</Label>
              <Select
                value={formData.decisionStatus}
                onChange={(e) => setFormData({ ...formData, decisionStatus: parseInt(e.target.value) })}
                required
              >
                <option value={CommitteeDecisionStatus.Pending}>ממתין להחלטה</option>
                <option value={CommitteeDecisionStatus.ApprovedPermanent}>אושר לצמיתות</option>
                <option value={CommitteeDecisionStatus.ApprovedForPeriod}>אושר לתקופה</option>
                <option value={CommitteeDecisionStatus.Rejected}>נדחה</option>
              </Select>
            </FormGroup>

            {isApproved && (
              <Section>
                <SectionTitle>פרטי אישור</SectionTitle>

                <FormGroup>
                  <Label>אחוז נכות *</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.disabilityPercentage || ''}
                    onChange={(e) => setFormData({ ...formData, disabilityPercentage: parseInt(e.target.value) || undefined })}
                    placeholder="0-100"
                    hasError={!!errors.disabilityPercentage}
                    required={isApproved}
                  />
                  {errors.disabilityPercentage && <ErrorMessage>{errors.disabilityPercentage}</ErrorMessage>}
                </FormGroup>

                {isApprovedForPeriod && (
                  <FormGroup>
                    <Label>תאריך סיום זכאות *</Label>
                    <Input
                      type="date"
                      value={formData.approvalEndDate ? formData.approvalEndDate.split('T')[0] : ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        approvalEndDate: e.target.value ? `${e.target.value}T00:00:00` : undefined
                      })}
                      hasError={!!errors.approvalEndDate}
                      required={isApprovedForPeriod}
                    />
                    {errors.approvalEndDate && <ErrorMessage>{errors.approvalEndDate}</ErrorMessage>}
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>סכום גמלה חודשי (₪)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.monthlyBenefitAmount || ''}
                    onChange={(e) => setFormData({ ...formData, monthlyBenefitAmount: parseFloat(e.target.value) || undefined })}
                    placeholder="0.00"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>רטרואקטיבי מתאריך</Label>
                  <Input
                    type="date"
                    value={formData.retroactiveFromDate ? formData.retroactiveFromDate.split('T')[0] : ''}
                    onChange={(e) => setFormData({ ...formData, retroactiveFromDate: e.target.value ? `${e.target.value}T00:00:00` : undefined })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>סכום רטרואקטיבי כולל (₪)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.retroactiveTotalAmount || ''}
                    onChange={(e) => setFormData({ ...formData, retroactiveTotalAmount: parseFloat(e.target.value) || undefined })}
                    placeholder="0.00"
                  />
                </FormGroup>
              </Section>
            )}

            <Section>
              <SectionTitle>הערות והערות החלטה</SectionTitle>

              <FormGroup>
                <Label>הערות החלטה</Label>
                <TextArea
                  value={formData.decisionNotes || ''}
                  onChange={(e) => setFormData({ ...formData, decisionNotes: e.target.value })}
                  placeholder="הערות נוספות לגבי ההחלטה..."
                />
              </FormGroup>
            </Section>
          </ModalBody>

          <ButtonContainer>
            <Button type="button" variant="secondary" onClick={onClose}>
              ביטול
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'שומר...' : 'שמירה'}
            </Button>
          </ButtonContainer>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default UpdateDecisionModal;
